import { Trophy, ShipWheel, Wrench, Users, Flag, CalendarRange } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";

const OFFERS = [
  { icon: Trophy, title: "Un sezon complet", text: "Toate etapele de regate de pe litoral, din mai până în septembrie 2027 — nu o cursă, ci un campionat întreg." },
  { icon: ShipWheel, title: "Barca de cursă", text: "Hope, un Sun Odyssey 42.2 pregătit și întreținut pentru competiție — stabil, iute și iertător cu echipajele noi." },
  { icon: Wrench, title: "Suport logistic", text: "Ne ocupăm de înscrieri, mentenanță și organizare. Tu te concentrezi pe tactică și echipaj." },
  { icon: Users, title: "Echipa ta", text: "Îți construiești propriul echipaj de 5–8 oameni: prieteni, colegi, cursanți — alegerea e a ta." },
];

const WANTED = [
  "Permis de navigație (minim categoria D)",
  "Experiență de bază la cârma unui velier",
  "Dorința de a conduce aceeași echipă tot sezonul",
  "Disponibilitate în weekendurile de cursă",
];

const STEPS = [
  { nr: "01", title: "Trimite solicitarea", desc: "Prin formularul de rezervare sau direct pe WhatsApp. Prezintă-te pe scurt: experiență și intenții." },
  { nr: "02", title: "Ne cunoaștem pe apă", desc: "O ieșire de probă cu Hope, ca să vezi barca și să vedem cum lucrezi la cârmă." },
  { nr: "03", title: "Îți formezi echipa", desc: "Adu-ți oamenii și veniți la antrenamentele de dinaintea sezonului." },
  { nr: "04", title: "Startul sezonului 2027", desc: "Prima linie de start. De aici, rezultatele vorbesc despre voi." },
];

const REGATE_PHOTOS = [
  {
    src: "/hope/regate-a.jpg",
    alt: "Flota de regată cu spinnakere colorate la start",
    caption: "Flota la start — spinnakere sus",
    span: "md:col-span-7 h-[360px] md:h-[480px]",
  },
  {
    src: "/hope/regate-d.jpg",
    alt: "Velierul Hope în cursă, cu echipajul pe bordaj",
    caption: "Hope în plină cursă",
    span: "md:col-span-5 h-[360px] md:h-[480px]",
  },
  {
    src: "/hope/regate-b.jpg",
    alt: "Satul de regată — zona de relaxare a echipajelor la mal",
    caption: "Satul de regată — atmosfera de la mal",
    span: "md:col-span-5 h-[320px] md:h-[420px]",
  },
  {
    src: "/hope/regate-c.jpg",
    alt: "Medalii și trofee de regată pregătite pentru festivitatea de premiere",
    caption: "Medaliile așteaptă podiumul",
    span: "md:col-span-7 h-[320px] md:h-[420px]",
  },
];

const Regate = () => (
  <div data-testid="regate-page">
    <PageHero
      testid="regate-hero"
      image="/hope/regate-e.jpg"
      alt="Velierul Hope în flota de regată, sub pânze pe Marea Neagră"
      overline="Regate · Sezonul 2027"
      titleLines={["Regate 2027.", "Aici te afirmi."]}
      subtitle="Ești skipper și vrei să te afirmi? Îți poți crea propria echipă și participa la tot sezonul de regate din 2027, la bordul lui Hope. Așteptăm solicitări."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <Reveal className="mb-16">
        <Overline className="mb-6">Ce Îți Oferim</Overline>
        <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
          Barca e a noastră. <span className="italic text-gold">Victoria poate fi a ta.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
        {OFFERS.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08} className="bg-abyss">
            <div className="h-full p-8 lg:p-10" data-testid={`regate-offer-${i + 1}`}>
              <item.icon className="h-5 w-5 text-gold" />
              <h3 className="mt-5 font-serif text-xl text-ivory">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{item.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="regate-gallery" className="border-t border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <Overline className="mb-6">Din Flotă</Overline>
            <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
              Așa arată o zi <span className="italic text-gold">de cursă.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist">
            Imagini reale din regatele la care a participat Hope — starturi,
            satul de regată și podiumul.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {REGATE_PHOTOS.map((photo, i) => (
            <Reveal key={photo.src} delay={i * 0.08} className={photo.span}>
              <figure
                data-testid={`regate-photo-${i + 1}`}
                className="group relative h-full w-full overflow-hidden border border-white/10"
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 via-transparent to-transparent" />
                <figcaption className="absolute bottom-0 left-0 p-5 text-[10px] uppercase tracking-[0.25em] text-ivory/80 lg:p-6 lg:text-xs">
                  {photo.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-32">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline className="mb-6">Pe Cine Căutăm</Overline>
            <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl">
              Skipperi cu foame <span className="italic text-gold">de linia de start.</span>
            </h2>
          </Reveal>
          <div className="mt-10">
            {WANTED.map((item, i) => (
              <Reveal key={item} delay={i * 0.08}>
                <div className="flex items-center gap-4 border-t border-white/10 py-5" data-testid={`regate-wanted-${i + 1}`}>
                  <Flag className="h-4 w-4 shrink-0 text-gold" />
                  <p className="text-sm text-ivory/80">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <Overline className="mb-6">Cum Funcționează</Overline>
          </Reveal>
          {STEPS.map((step, i) => (
            <Reveal key={step.nr} delay={i * 0.1}>
              <div className="flex gap-8 border-t border-white/10 py-8" data-testid={`regate-step-${i + 1}`}>
                <p className="w-14 shrink-0 font-serif text-3xl font-light text-gold/50">{step.nr}</p>
                <div>
                  <h3 className="font-serif text-xl text-ivory">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <p className="mt-6 flex items-center gap-3 border border-gold/30 p-6 text-sm leading-relaxed text-mist">
              <CalendarRange className="h-4 w-4 shrink-0 text-gold" />
              Costurile de participare se stabilesc împreună, în funcție de etapele alese și de mărimea echipei.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <CtaBanner
      title="Așteptăm solicitări."
      subtitle="Locurile pentru sezonul 2027 sunt limitate — barca e una singură, iar calendarul de regate nu așteaptă. Scrie-ne sau trimite cererea din formularul de contact."
    />
  </div>
);

export default Regate;
